import { abrePDF } from "../../../utils/pdf";
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Table, Input, Label, ButtonGroup, Col, Badge } from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import moment from "moment";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
// "2026-09" → "Septiembre 2026"
const nombreMes = (m) => {
  const [y, mm] = String(m || "").split("-");
  return MESES[Number(mm) - 1] ? `${MESES[Number(mm) - 1]} ${y}` : m;
};

/**
 * Utilidad mes por mes, con el desglose de cada resta. Cada renglón es
 * exactamente lo que da el Reporte de Utilidades para ese mes (mismo cálculo
 * en el back), así que se pueden cuadrar uno contra otro.
 */
function ReporteUtilidadesMensual() {
  const { user } = useContext(AuthContext);
  const URL_REPORTE_UTILIDADES = process.env.REACT_APP_URL_REPORTE_UTILIDADES;

  // Por defecto: de enero del año en curso al mes actual
  const [mesInicio, setMesInicio] = useState(moment().format("YYYY") + "-01");
  const [mesFin, setMesFin] = useState(moment().format("YYYY-MM"));
  const [renglones, setRenglones] = useState([]);
  const [totales, setTotales] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [verAyuda, setVerAyuda] = useState(false);

  const money = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(v) || 0);
  const pct = (v) => `${(Number(v) || 0).toFixed(2)}%`;

  useEffect(() => {
    if (!mesInicio || !mesFin) return;
    if (mesInicio > mesFin) {
      setError("El mes inicial es posterior al final");
      setRenglones([]);
      setTotales(null);
      return;
    }
    let vivo = true;
    setCargando(true);
    setError("");
    const inicio = `${mesInicio}-01`;
    const fin = moment(`${mesFin}-01`).endOf("month").format("YYYY-MM-DD");
    axios
      .get(`${URL_REPORTE_UTILIDADES}Mensual/${inicio}/${fin}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((res) => {
        if (!vivo) return;
        setRenglones(res.data?.renglones || []);
        setTotales(res.data?.totales || null);
      })
      .catch((err) => {
        console.log("[utilidades mensual]", err);
        if (!vivo) return;
        setError(err?.response?.data?.message || "No se pudo generar el reporte");
        setRenglones([]);
        setTotales(null);
      })
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [URL_REPORTE_UTILIDADES, mesInicio, mesFin]);

  // Utilidad bruta = lo que deja la mercancía antes de comisiones y gastos
  const bruta = (r) => Number(r.venta || 0) - Number(r.costo || 0);
  const comisiones = (r) => Number(r.comision || 0) + Number(r.comisionEstimada || 0);

  const titulo = `${nombreMes(mesInicio)} – ${nombreMes(mesFin)}`;

  function PDFTabla() {
    const doc = new jsPDF("landscape", "mm", "a4", true);
    const img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 240, 12, 35, 15);
    doc.text("Utilidades por Mes", 15, 20);
    doc.text(titulo, 15, 27);
    doc.autoTable({
      head: [[
        "Mes", "Notas", "Venta", "Costo", "Utilidad bruta", "Comisión",
        "Com. est.", "G. directo", "G. general", "Utilidad", "Margen",
      ]],
      body: renglones.map((r) => [
        nombreMes(r.mes),
        r.notas,
        money(r.venta),
        money(r.costo),
        money(bruta(r)),
        money(r.comision),
        money(r.comisionEstimada),
        money(r.gastoDirecto),
        money(r.gastoGeneral),
        money(r.utilidad),
        pct(r.margen),
      ]),
      foot: totales
        ? [[
            "TOTAL",
            totales.notas,
            money(totales.venta),
            money(totales.costo),
            money(bruta(totales)),
            money(totales.comision),
            money(totales.comisionEstimada),
            money(totales.gastoDirecto),
            money(totales.gastoGeneral),
            money(totales.utilidad),
            pct(totales.margen),
          ]]
        : undefined,
      headStyles: { fillColor: ["#B02307"], lineWidth: 0 },
      footStyles: { fillColor: ["#B02307"] },
      theme: "grid",
      startY: 34,
      styles: { fontSize: 8 },
      columnStyles: Object.fromEntries([1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => [i, { halign: "right" }])),
      showFoot: "lastPage",
    });
    abrePDF(doc, "UtilidadesPorMes.pdf");
  }

  function excel() {
    new ExportJsonExcel({
      fileName: `UtilidadesPorMes-${mesInicio}-${mesFin}`,
      datas: [
        {
          sheetData: [
            ...renglones.map((r) => ({
              Mes: nombreMes(r.mes),
              Notas: r.notas,
              Venta: r.venta,
              Costo: r.costo,
              UtilidadBruta: Number(bruta(r).toFixed(2)),
              Comision: r.comision,
              ComisionEstimada: r.comisionEstimada,
              GastoDirecto: r.gastoDirecto,
              GastoGeneral: r.gastoGeneral,
              Viaticos: r.viaticos,
              Utilidad: r.utilidad,
              Margen: r.margen,
            })),
            ...(totales
              ? [{
                  Mes: "TOTAL",
                  Notas: totales.notas,
                  Venta: totales.venta,
                  Costo: totales.costo,
                  UtilidadBruta: Number(bruta(totales).toFixed(2)),
                  Comision: totales.comision,
                  ComisionEstimada: totales.comisionEstimada,
                  GastoDirecto: totales.gastoDirecto,
                  GastoGeneral: totales.gastoGeneral,
                  Viaticos: totales.viaticos,
                  Utilidad: totales.utilidad,
                  Margen: totales.margen,
                }]
              : []),
          ],
          sheetName: "UtilidadesPorMes",
          sheetFilter: [
            "Mes", "Notas", "Venta", "Costo", "UtilidadBruta", "Comision", "ComisionEstimada",
            "GastoDirecto", "GastoGeneral", "Viaticos", "Utilidad", "Margen",
          ],
          sheetHeader: [
            "Mes", "Notas", "Venta", "Costo", "Utilidad bruta", "Comisión", "Comisión est.",
            "Gasto directo", "Gasto general", "de ello viáticos sin ventas", "Utilidad", "Margen %",
          ],
        },
      ],
    }).saveExcel();
  }

  // Barra horizontal de la utilidad de cada mes, para ver la tendencia
  const maxAbs = Math.max(1, ...renglones.map((r) => Math.abs(Number(r.utilidad) || 0)));

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_admin ? (
        <div className="card container col-12">
          <Row>
            <Col md={3}>
              <ButtonGroup>
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla} disabled={!renglones.length}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel} disabled={!renglones.length}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
            <Col sm={9}>
              <ButtonGroup id="logoutBoton">
                <Button href="/MenuVentas" className="btn btn-danger" id="botonListado">
                  Regresar
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Utilidades por Mes</h3>

          <Row className="mb-2">
            <Col md={2}>
              <Label className="mr-sm-2">Mes inicial</Label>
              <Input bsSize="sm" type="month" value={mesInicio} onChange={(e) => setMesInicio(e.target.value)} />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Mes final</Label>
              <Input bsSize="sm" type="month" value={mesFin} onChange={(e) => setMesFin(e.target.value)} />
            </Col>
          </Row>

          {totales ? (
            <Row className="mb-3" align="center">
              <Col md={2}>
                <Label className="mr-sm-2">Venta</Label>
                <h5><Badge color="info" className="BadgeSize badge-pill">{money(totales.venta)}</Badge></h5>
              </Col>
              <Col md={2}>
                <Label className="mr-sm-2">Costo</Label>
                <h5><Badge color="secondary" className="BadgeSize badge-pill">{money(totales.costo)}</Badge></h5>
              </Col>
              <Col md={2}>
                <Label className="mr-sm-2">Comisiones</Label>
                <h5><Badge color="warning" className="BadgeSize badge-pill">{money(comisiones(totales))}</Badge></h5>
              </Col>
              <Col md={2}>
                <Label className="mr-sm-2">Gastos (dir. + gral.)</Label>
                <h5>
                  <Badge color="warning" className="BadgeSize badge-pill">
                    {money(Number(totales.gastoDirecto) + Number(totales.gastoGeneral))}
                  </Badge>
                </h5>
              </Col>
              <Col md={2}>
                <Label className="mr-sm-2">Utilidad</Label>
                <h5>
                  <Badge color={totales.utilidad >= 0 ? "success" : "danger"} className="BadgeSize badge-pill">
                    {money(totales.utilidad)}
                  </Badge>
                </h5>
              </Col>
              <Col md={2}>
                <Label className="mr-sm-2">Margen</Label>
                <h5>
                  <Badge color={totales.margen >= 0 ? "success" : "danger"} className="BadgeSize badge-pill">
                    {pct(totales.margen)}
                  </Badge>
                </h5>
              </Col>
            </Row>
          ) : null}

          <div className="mb-2">
            <Button size="sm" color="link" style={{ paddingLeft: 0 }} onClick={() => setVerAyuda(!verAyuda)}>
              {verAyuda ? "Ocultar" : "¿Qué va en cada columna?"}
            </Button>
            {verAyuda ? (
              <Table size="sm" bordered className="table-responsive-xl mt-1">
                <tbody style={{ fontSize: 13 }}>
                  <tr><td className="negrita">Venta</td><td>Total de las notas del mes, con IVA y descuento.</td></tr>
                  <tr><td className="negrita">Costo</td><td>Costo de la mercancía surtida, sellado al surtir.</td></tr>
                  <tr><td className="negrita">Utilidad bruta</td><td>Venta − Costo: lo que deja la mercancía antes de comisiones y gastos.</td></tr>
                  <tr><td className="negrita">Comisión</td><td>Gasto directo de la venta: la pagada, con su ajuste (bono o descuento).</td></tr>
                  <tr><td className="negrita">Com. est.</td><td>Notas cuya comisión aún no se paga: lo que le tocaría al vendedor según su %.</td></tr>
                  <tr><td className="negrita">G. directo</td><td>Envíos y demás gastos cargados a cada nota.</td></tr>
                  <tr><td className="negrita">G. general</td><td>Gastos del mes que no son de ninguna venta: los capturados en Gastos más los viáticos pagados.</td></tr>
                  <tr style={{ backgroundColor: "#eef7f1" }}>
                    <td className="negrita">Utilidad</td>
                    <td><b>Venta − Costo − Comisión − Com. est. − G. directo − G. general</b>. El margen es sobre la venta del mes.</td>
                  </tr>
                </tbody>
              </Table>
            ) : null}
          </div>

          {error ? <p style={{ color: "#dc3545" }}>{error}</p> : null}

          <Table size="sm" striped bordered className="table-responsive-xl">
            <thead>
              <tr align="center">
                <th className="tituloTabla">Mes</th>
                <th className="tituloTabla">Notas</th>
                <th className="tituloTabla">Venta</th>
                <th className="tituloTabla">Costo</th>
                <th className="tituloTabla">Utilidad bruta</th>
                <th className="tituloTabla">Comisión</th>
                <th className="tituloTabla">Com. est.</th>
                <th className="tituloTabla">G. directo</th>
                <th className="tituloTabla">G. general</th>
                <th className="tituloTabla">Utilidad</th>
                <th className="tituloTabla">Margen</th>
                <th className="tituloTabla" style={{ minWidth: 140 }}>Tendencia</th>
              </tr>
            </thead>
            <tbody>
              {cargando ? (
                <tr><td colSpan={12}>Calculando mes por mes…</td></tr>
              ) : !renglones.length ? (
                <tr><td colSpan={12}>Sin datos en este rango.</td></tr>
              ) : (
                renglones.map((r) => {
                  const util = Number(r.utilidad) || 0;
                  const ancho = Math.round((Math.abs(util) / maxAbs) * 100);
                  return (
                    <tr key={r.mes} style={util < 0 ? { backgroundColor: "#f8d7da" } : undefined}>
                      <td className="negrita">{nombreMes(r.mes)}</td>
                      <td align="right">{r.notas}</td>
                      <td align="right">{money(r.venta)}</td>
                      <td align="right">{money(r.costo)}</td>
                      <td align="right">{money(bruta(r))}</td>
                      <td align="right">{money(r.comision)}</td>
                      <td align="right" style={{ color: r.comisionEstimada > 0 ? "#b8860b" : undefined }}>
                        {money(r.comisionEstimada)}
                      </td>
                      <td align="right">{money(r.gastoDirecto)}</td>
                      <td align="right">
                        {money(r.gastoGeneral)}
                        {Number(r.viaticos) > 0 ? (
                          <div style={{ fontSize: 11, color: "#666" }}>incl. viáticos sin ventas {money(r.viaticos)}</div>
                        ) : null}
                      </td>
                      <td align="right" className="negrita" style={{ color: util < 0 ? "#dc3545" : "#28a745" }}>
                        {money(util)}
                      </td>
                      <td align="right">{pct(r.margen)}</td>
                      <td>
                        <div
                          title={money(util)}
                          style={{
                            height: 10,
                            width: `${ancho}%`,
                            minWidth: util ? 2 : 0,
                            background: util < 0 ? "#dc3545" : "#28a745",
                            borderRadius: 3,
                          }}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
              {!cargando && totales && renglones.length ? (
                <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                  <td>TOTAL</td>
                  <td align="right">{totales.notas}</td>
                  <td align="right">{money(totales.venta)}</td>
                  <td align="right">{money(totales.costo)}</td>
                  <td align="right">{money(bruta(totales))}</td>
                  <td align="right">{money(totales.comision)}</td>
                  <td align="right">{money(totales.comisionEstimada)}</td>
                  <td align="right">{money(totales.gastoDirecto)}</td>
                  <td align="right">{money(totales.gastoGeneral)}</td>
                  <td align="right" style={{ color: totales.utilidad < 0 ? "#dc3545" : "#28a745" }}>
                    {money(totales.utilidad)}
                  </td>
                  <td align="right">{pct(totales.margen)}</td>
                  <td></td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>
      ) : undefined}
    </>
  );
}

export default ReporteUtilidadesMensual;
